import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { 
  X, Users, CreditCard, Settings, Building,
  UserPlus, Mail, Trash2, RefreshCw, Crown, Check,
  Clock, AlertCircle, Copy, Eye, Shield,
  ChevronRight, ExternalLink, Zap
} from 'lucide-react';
import { useTeam, TeamRole, TeamMember } from '../../contexts/TeamContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';

interface OrganizationPanelProps {
  onClose: () => void;
  onUpgrade?: () => void;
}

type TabType = 'team' | 'billing' | 'settings';

const ROLE_LABELS: Record<TeamRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  viewer: 'Viewer',
};

const ROLE_COLORS: Record<TeamRole, string> = {
  owner: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  admin: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  manager: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  viewer: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

const ROLE_ICONS: Record<TeamRole, React.ReactNode> = {
  owner: <Shield className="w-3.5 h-3.5" />,
  admin: <Crown className="w-3.5 h-3.5" />,
  manager: <Settings className="w-3.5 h-3.5" />,
  viewer: <Eye className="w-3.5 h-3.5" />,
};

export function OrganizationPanel({ onClose, onUpgrade }: OrganizationPanelProps) {
  const { subscription } = useAuth();
  const { isEnterprise, isPro } = usePlanFeatures();
  const [activeTab, setActiveTab] = useState<TabType>(isEnterprise ? 'team' : 'billing');

  const plan = subscription?.plan || 'free';

  // Determine which tabs to show based on plan
  const tabs = [
    { id: 'team' as TabType, label: 'Team', icon: Users, show: isEnterprise },
    { id: 'billing' as TabType, label: 'Subscription', icon: CreditCard, show: true },
    { id: 'settings' as TabType, label: 'Settings', icon: Settings, show: isPro || isEnterprise },
  ].filter(tab => tab.show);

  const planColors = {
    free: 'from-slate-600 to-slate-700',
    pro: 'from-blue-600 to-cyan-600',
    enterprise: 'from-purple-600 to-pink-600',
  };

  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className={`bg-gradient-to-r ${planColors[plan as keyof typeof planColors] || planColors.free} px-6 py-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Building className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Organization</h1>
              <p className="text-white/70 text-sm capitalize">{plan} Plan</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-700 bg-slate-800/50">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-all relative
                ${activeTab === tab.id 
                  ? 'text-white' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {activeTab === tab.id && (
                <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r ${planColors[plan as keyof typeof planColors] || planColors.free}`} />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'team' && isEnterprise && <TeamTab />}
          {activeTab === 'billing' && <BillingTab onUpgrade={onUpgrade} />}
          {activeTab === 'settings' && <SettingsTab />}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ============================================
// TEAM TAB (Enterprise Only)
// ============================================
function TeamTab() {
  const { 
    teamMembers, 
    pendingInvites, 
    maxMembers,
    inviteMember, 
    getInviteLink,
    removeMember,
    cancelInvite,
    resendInvite,
    canInviteMembers,
    canManageTeam,
    currentUserRole,
  } = useTeam();

  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<TeamRole>('viewer');
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [lastInviteLink, setLastInviteLink] = useState<string | null>(null);

  const handleInvite = () => {
    if (!inviteEmail.trim()) return;
    
    const invite = inviteMember(inviteEmail.trim(), inviteRole);
    const link = getInviteLink(invite.id);
    
    if (link) {
      navigator.clipboard.writeText(link);
      setLastInviteLink(link);
    }
    
    setInviteEmail('');
    setInviteRole('viewer');
    setInviteSuccess(true);
  };

  const handleCopyLink = (inviteId: string) => {
    const link = getInviteLink(inviteId);
    if (link) {
      navigator.clipboard.writeText(link);
      setCopiedLink(inviteId);
      setTimeout(() => setCopiedLink(null), 3000);
    }
  };

  const handleRemoveMember = (member: TeamMember) => {
    if (window.confirm(`Remove ${member.name || member.email} from the team?`)) {
      removeMember(member.id);
    }
  };

  const totalMembers = teamMembers.length + pendingInvites.length;

  return (
    <div className="space-y-6">
      {/* Your Role Banner */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${ROLE_COLORS[currentUserRole]}`}>
              {ROLE_ICONS[currentUserRole]}
            </div>
            <div>
              <p className="text-sm text-slate-400">Your Role</p>
              <p className="text-white font-medium">{ROLE_LABELS[currentUserRole]}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-400">Team Size</p>
            <p className="text-white font-medium">{totalMembers} / {maxMembers}</p>
          </div>
        </div>
      </div>

      {/* Invite Section */}
      {canInviteMembers && (
        <div className="bg-slate-800/30 rounded-xl border border-slate-700 overflow-hidden">
          <button
            onClick={() => {
              setShowInviteForm(!showInviteForm);
              setInviteSuccess(false);
              setLastInviteLink(null);
            }}
            className="w-full flex items-center justify-between p-4 hover:bg-slate-700/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-purple-400" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">Invite Team Member</p>
                <p className="text-sm text-slate-400">Add colleagues to your organization</p>
              </div>
            </div>
            <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${showInviteForm ? 'rotate-90' : ''}`} />
          </button>
          
          {showInviteForm && (
            <div className="p-4 border-t border-slate-700 bg-slate-800/50">
              {inviteSuccess ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-purple-400">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Invite Created!</span>
                  </div>
                  <p className="text-sm text-slate-400">
                    Share this link with your team member. The link has been copied to your clipboard.
                  </p>
                  {lastInviteLink && (
                    <div className="bg-slate-900 rounded-lg p-3 flex items-center gap-2">
                      <code className="text-xs text-slate-300 flex-1 truncate">{lastInviteLink}</code>
                      <button
                        onClick={() => {
                          if (lastInviteLink) navigator.clipboard.writeText(lastInviteLink);
                        }}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" />
                        Copy
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <p className="text-xs text-amber-200">
                      Email notifications coming soon. Please share the link manually for now.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setInviteSuccess(false);
                      setLastInviteLink(null);
                    }}
                    className="text-sm text-purple-400 hover:text-purple-300"
                  >
                    Invite another member
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-xs text-slate-400 mb-1.5">Email Address</label>
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="colleague@company.com"
                        className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg 
                                 text-white placeholder-slate-500 focus:outline-none focus:ring-2 
                                 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1.5">Role</label>
                      <select
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value as TeamRole)}
                        className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg 
                                 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 
                                 focus:border-transparent cursor-pointer"
                      >
                        <option value="viewer">Viewer</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Role descriptions */}
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className={`p-2 rounded-lg border ${inviteRole === 'viewer' ? 'bg-slate-700/50 border-purple-500/50' : 'border-slate-700'}`}>
                      <p className="text-slate-300 font-medium">Viewer</p>
                      <p className="text-slate-500">View-only access</p>
                    </div>
                    <div className={`p-2 rounded-lg border ${inviteRole === 'manager' ? 'bg-slate-700/50 border-purple-500/50' : 'border-slate-700'}`}>
                      <p className="text-slate-300 font-medium">Manager</p>
                      <p className="text-slate-500">Upload & manage</p>
                    </div>
                    <div className={`p-2 rounded-lg border ${inviteRole === 'admin' ? 'bg-slate-700/50 border-purple-500/50' : 'border-slate-700'}`}>
                      <p className="text-slate-300 font-medium">Admin</p>
                      <p className="text-slate-500">Full team access</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleInvite}
                      disabled={!inviteEmail.trim()}
                      className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 
                               hover:from-purple-500 hover:to-pink-500 disabled:from-slate-600 disabled:to-slate-600
                               disabled:cursor-not-allowed text-white rounded-lg transition-all font-medium"
                    >
                      <Mail className="w-4 h-4" />
                      Create Invite
                    </button>
                    <button
                      onClick={() => setShowInviteForm(false)}
                      className="px-4 py-2.5 text-slate-400 hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Team Members */}
      <div>
        <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
          <Users className="w-4 h-4" />
          Team Members ({teamMembers.length})
        </h3>
        <div className="space-y-2">
          {teamMembers.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-slate-600 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center">
                  <span className="text-lg font-medium text-white">
                    {(member.name || member.email).charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-white font-medium">{member.name || 'Unnamed'}</p>
                  <p className="text-sm text-slate-400">{member.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${ROLE_COLORS[member.role]}`}>
                  {ROLE_ICONS[member.role]}
                  {ROLE_LABELS[member.role]}
                </span>
                {canManageTeam && member.role !== 'owner' && (
                  <button
                    onClick={() => handleRemoveMember(member)}
                    className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Remove member"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          
          {teamMembers.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No team members yet</p>
              <p className="text-sm mt-1">Invite your colleagues to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Pending Invites ({pendingInvites.length})
          </h3>
          <div className="space-y-2">
            {pendingInvites.map((invite) => (
              <div
                key={invite.id}
                className="flex items-center justify-between p-4 bg-slate-800/30 rounded-xl border border-dashed border-slate-600"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-700/50 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-slate-300">{invite.email}</p>
                    <p className="text-xs text-slate-500">
                      Invited as {ROLE_LABELS[invite.role]} • Expires in 7 days
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleCopyLink(invite.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                      ${copiedLink === invite.id 
                        ? 'bg-purple-500/20 text-purple-400' 
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                  >
                    {copiedLink === invite.id ? (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        Copy Link
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => resendInvite(invite.id)}
                    className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                    title="Resend invite"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => cancelInvite(invite.id)}
                    className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Cancel invite"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// BILLING TAB
// ============================================
function BillingTab({ onUpgrade }: { onUpgrade?: () => void }) {
  const { subscription } = useAuth();
  const plan = subscription?.plan || 'free';
  const status = subscription?.status || 'active';

  return (
    <div className="space-y-6">
      {/* Current Plan Card */}
      <div className={`rounded-xl p-6 border ${
        plan === 'enterprise' ? 'bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30' :
        plan === 'pro' ? 'bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/30' :
        'bg-slate-800/50 border-slate-700'
      }`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-400 mb-1">Current Plan</p>
            <h2 className={`text-3xl font-bold ${
              plan === 'enterprise' ? 'text-purple-400' :
              plan === 'pro' ? 'text-blue-400' :
              'text-slate-300'
            }`}>
              {plan === 'enterprise' ? 'Enterprise' : plan === 'pro' ? 'Pro' : 'Free'}
            </h2>
            <p className="text-slate-400 mt-1">
              {plan === 'enterprise' ? '$149/month' : plan === 'pro' ? '$49/month' : 'Free forever'}
            </p>
          </div>
          <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${
            status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
          }`}>
            {status === 'active' ? '● Active' : '○ Inactive'}
          </div>
        </div>

        {/* Plan Features */}
        <div className="mt-6 pt-6 border-t border-slate-700/50">
          <p className="text-sm text-slate-400 mb-3">Your plan includes:</p>
          <div className="grid grid-cols-2 gap-2">
            {plan === 'free' && (
              <>
                <Feature text="3 reports/month" />
                <Feature text="Basic analytics" />
                <Feature text="Email support" />
              </>
            )}
            {plan === 'pro' && (
              <>
                <Feature text="Unlimited reports" />
                <Feature text="PDF & Excel export" />
                <Feature text="Webhook integrations" />
                <Feature text="Custom alerts" />
                <Feature text="Priority support" />
              </>
            )}
            {plan === 'enterprise' && (
              <>
                <Feature text="Everything in Pro" />
                <Feature text="Team accounts (10)" />
                <Feature text="Multi-location" />
                <Feature text="API access" />
                <Feature text="Google Drive sync" />
                <Feature text="Dedicated support" />
              </>
            )}
          </div>
        </div>

        {/* Upgrade Button */}
        {plan !== 'enterprise' && onUpgrade && (
          <button 
            onClick={onUpgrade}
            className="mt-6 w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
          >
            <Zap className="w-4 h-4" />
            Upgrade to {plan === 'free' ? 'Pro' : 'Enterprise'}
          </button>
        )}
      </div>

      {/* Plan Comparison */}
      {plan !== 'enterprise' && (
        <div>
          <h3 className="text-sm font-medium text-slate-400 mb-3">Compare Plans</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {plan === 'free' && (
              <PlanCard 
                name="Pro"
                price="$49"
                features={['Unlimited reports', 'Export to PDF/Excel', 'Webhooks', 'Custom alerts']}
                color="blue"
                recommended
              />
            )}
            <PlanCard 
              name="Enterprise"
              price="$149"
              features={['Team accounts', 'Multi-location', 'API access', 'Dedicated support']}
              color="purple"
              recommended={plan === 'pro'}
            />
          </div>
        </div>
      )}

      {/* Billing History */}
      {plan !== 'free' && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
            <h3 className="text-white font-medium">Billing History</h3>
            <button className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">
              <ExternalLink className="w-3.5 h-3.5" />
              Manage in Stripe
            </button>
          </div>
          <div className="p-4 text-center text-slate-400 text-sm">
            Billing history will appear here once connected to Stripe.
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// SETTINGS TAB
// ============================================
function SettingsTab() {
  const { user } = useAuth();

  return (
    <div className="space-y-6">
      {/* Organization Info */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700">
          <h3 className="text-white font-medium">Organization Details</h3>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Organization Name</label>
            <input
              type="text"
              defaultValue="My Organization"
              className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Owner Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-400 cursor-not-allowed"
            />
          </div>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700">
          <h3 className="text-white font-medium">Notifications</h3>
        </div>
        <div className="p-4 space-y-4">
          <ToggleSetting 
            label="Email Alerts" 
            description="Receive email notifications for compliance alerts"
            defaultChecked
          />
          <ToggleSetting 
            label="Weekly Reports" 
            description="Get a weekly summary of your analytics"
            defaultChecked={false}
          />
          <ToggleSetting 
            label="Team Activity" 
            description="Notifications when team members upload reports"
            defaultChecked
          />
        </div>
      </div>

      {/* Save Button */}
      <button className="w-full py-3 bg-purple-600 hover:bg-purple-500 text-white font-medium rounded-lg transition-colors">
        Save Changes
      </button>
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================
function Feature({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-300">
      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
      {text}
    </div>
  );
}

function PlanCard({ name, price, features, color, recommended }: {
  name: string;
  price: string;
  features: string[];
  color: 'blue' | 'purple';
  recommended?: boolean;
}) {
  const colors = {
    blue: 'border-blue-500/30 bg-blue-500/5',
    purple: 'border-purple-500/30 bg-purple-500/5',
  };
  const textColors = {
    blue: 'text-blue-400',
    purple: 'text-purple-400',
  };

  return (
    <div className={`rounded-xl p-4 border ${colors[color]} relative`}>
      {recommended && (
        <div className={`absolute -top-2 left-4 px-2 py-0.5 ${color === 'blue' ? 'bg-blue-500' : 'bg-purple-500'} text-white text-xs font-medium rounded`}>
          Recommended
        </div>
      )}
      <h4 className={`font-medium ${textColors[color]}`}>{name}</h4>
      <p className="text-2xl font-bold text-white mt-1">{price}<span className="text-sm text-slate-400">/mo</span></p>
      <ul className="mt-3 space-y-1.5">
        {features.map((f, i) => (
          <li key={i} className="text-sm text-slate-400 flex items-center gap-2">
            <Check className={`w-3.5 h-3.5 ${textColors[color]}`} />
            {f}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ToggleSetting({ label, description, defaultChecked }: {
  label: string;
  description: string;
  defaultChecked?: boolean;
}) {
  const [checked, setChecked] = useState(defaultChecked || false);

  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm text-white">{label}</p>
        <p className="text-xs text-slate-400">{description}</p>
      </div>
      <button
        onClick={() => setChecked(!checked)}
        className={`w-11 h-6 rounded-full transition-colors ${checked ? 'bg-purple-500' : 'bg-slate-600'}`}
      >
        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}
